import {
  AgentIcon,
  AlertIcon,
  ClockIcon,
  DotFillIcon,
  StopIcon,
  SkipIcon,
  CheckCircleIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  ArchiveIcon,
  IterationsIcon,
  TerminalIcon,
  VscodeIcon,
  XCircleIcon,
} from '@primer/octicons-react'
import type {UITask} from '../types/task'
import type {ActiveTab} from '../types/filter-types'
import type {AgentInfo} from '../types/session'

// --- Shared types ---

export type SortDirection = 'newest' | 'oldest'

export type OpenStatus =
  | 'in-progress'
  | 'queued'
  | 'idle'
  | 'needs-attention'
  | 'failed'
  | 'completed'
  | 'cancelled'
  | 'timed_out'
export type ClosedStatus = 'merged' | 'closed' | 'archived'
export type DetailedStatus = OpenStatus | ClosedStatus

export interface FilterOption {
  value: string
  label: string
  icon?: React.ComponentType<{className?: string}>
  avatarUrl?: string
  group?: 'agents' | 'custom'
  /** When set, selecting this option toggles all rawValues instead of just value. */
  rawValues?: string[]
  /** The agent type (cloud, cli, vscode, etc.) for agent filter options. */
  agentType?: string
}

export interface ParsedFilters {
  status: ActiveTab
  detailedStatuses: string[]
  creators: string[]
  excludedCreators: string[]
  agents: string[]
  types: string[]
  sort: SortDirection
  freeText: string
}

// --- Filter status constants (UI layer, used in URLs) ---

export const FILTER_STATUS_IN_PROGRESS = 'in-progress' as const
export const FILTER_STATUS_QUEUED = 'queued' as const
export const FILTER_STATUS_IDLE = 'idle' as const
export const FILTER_STATUS_NEEDS_ATTENTION = 'needs-attention' as const
export const FILTER_STATUS_FAILED = 'failed' as const
export const FILTER_STATUS_COMPLETED = 'completed' as const
export const FILTER_STATUS_CANCELLED = 'cancelled' as const
export const FILTER_STATUS_TIMED_OUT = 'timed_out' as const
export const FILTER_STATUS_MERGED = 'merged' as const
export const FILTER_STATUS_CLOSED = 'closed' as const
export const FILTER_STATUS_ARCHIVED = 'archived' as const

// Legacy snake_case filter values (for backward-compatible URL parsing)
export const LEGACY_FILTER_STATUS_IN_PROGRESS = 'in_progress' as const
export const LEGACY_FILTER_STATUS_NEEDS_ATTENTION = 'needs_attention' as const

// --- Constants ---

export const OPEN_STATUSES = new Set<string>([
  FILTER_STATUS_IN_PROGRESS,
  FILTER_STATUS_QUEUED,
  FILTER_STATUS_IDLE,
  FILTER_STATUS_NEEDS_ATTENTION,
  FILTER_STATUS_FAILED,
  FILTER_STATUS_COMPLETED,
  FILTER_STATUS_CANCELLED,
  FILTER_STATUS_TIMED_OUT,
])
export const CLOSED_STATUSES = new Set<string>([FILTER_STATUS_MERGED, FILTER_STATUS_CLOSED, FILTER_STATUS_ARCHIVED])
export const ALL_STATUSES = new Set<string>([...OPEN_STATUSES, ...CLOSED_STATUSES])

export const ME_ALIAS = '@me'
export const DEFAULT_FILTER_QUERY = 'is:open'

// Filter qualifier prefixes
export const IS_QUALIFIER = 'is:'
export const STATUS_QUALIFIER = 'status:'
export const AUTHOR_QUALIFIER = 'author:'
export const AGENT_QUALIFIER = 'agent:'
export const TYPE_QUALIFIER = 'type:'
export const SORT_QUALIFIER = 'sort:'

export const EXCLUDE_PREFIX = '-'

/** Valid agent type/group values for the Type filter dropdown. */
export const VALID_AGENT_TYPES = new Set(['cloud', 'cli', 'vscode'])

export const TYPE_OPTIONS: FilterOption[] = [
  {value: 'cloud', label: 'Cloud agents', icon: AgentIcon},
  {value: 'cli', label: 'CLI', icon: TerminalIcon},
  {value: 'vscode', label: 'VS Code', icon: VscodeIcon},
]

export function getTypeOptionsForAgents(agents: AgentInfo[]): FilterOption[] {
  const presentGroups = new Set<string>(agents.map(agent => agent.group ?? 'cloud'))
  return TYPE_OPTIONS.filter(option => presentGroups.has(option.value))
}

const OPEN_STATUS_OPTIONS: FilterOption[] = [
  {value: FILTER_STATUS_IN_PROGRESS, label: 'In progress', icon: IterationsIcon},
  {value: FILTER_STATUS_QUEUED, label: 'Queued', icon: ClockIcon},
  {value: FILTER_STATUS_IDLE, label: 'Idle', icon: DotFillIcon},
  {value: FILTER_STATUS_NEEDS_ATTENTION, label: 'Needs attention', icon: AlertIcon},
  {value: FILTER_STATUS_FAILED, label: 'Failed', icon: StopIcon},
  {value: FILTER_STATUS_COMPLETED, label: 'Completed', icon: CheckCircleIcon},
  {value: FILTER_STATUS_CANCELLED, label: 'Cancelled', icon: SkipIcon},
  {value: FILTER_STATUS_TIMED_OUT, label: 'Timed out', icon: StopIcon},
]

const CLOSED_STATUS_OPTIONS: FilterOption[] = [
  {value: FILTER_STATUS_MERGED, label: 'Merged', icon: GitMergeIcon},
  {value: FILTER_STATUS_CLOSED, label: 'Closed', icon: GitPullRequestClosedIcon},
  {value: FILTER_STATUS_ARCHIVED, label: 'Archived', icon: ArchiveIcon},
]

const ALL_STATUS_OPTIONS: FilterOption[] = [...OPEN_STATUS_OPTIONS, ...CLOSED_STATUS_OPTIONS]

// --- Backwards-compatible normalization ---

const LEGACY_STATUS_ALIASES: Record<string, string> = {
  [LEGACY_FILTER_STATUS_IN_PROGRESS]: FILTER_STATUS_IN_PROGRESS,
  [LEGACY_FILTER_STATUS_NEEDS_ATTENTION]: FILTER_STATUS_NEEDS_ATTENTION,
}

/**
 * Normalizes a detailed status value from legacy snake_case to kebab-case.
 * Accepts both formats for backwards compatibility with existing bookmarks/URLs.
 */
export function normalizeDetailedStatus(status: string): string {
  return LEGACY_STATUS_ALIASES[status] ?? status
}

/**
 * UI filter values (kebab-case) differ from API session state values (snake_case).
 */
export function mapDetailedStatusToSessionState(status: string): string {
  const normalized = normalizeDetailedStatus(status)
  if (normalized === FILTER_STATUS_NEEDS_ATTENTION) return 'waiting_for_user'
  if (normalized === FILTER_STATUS_IN_PROGRESS) return 'in_progress'
  return normalized
}

// Consolidated status options (behind copilot_mission_control_filters_followups FF)
// - "Idle" merged into "Completed"
// - "Failed", "Timed out", "Cancelled" collapsed into "Unsuccessful"
// - "Queued" conditionally shown only when cloud agent type is selected or no type filter
const CONSOLIDATED_OPEN_STATUS_OPTIONS: FilterOption[] = [
  {value: FILTER_STATUS_IN_PROGRESS, label: 'In progress', icon: IterationsIcon},
  {value: FILTER_STATUS_NEEDS_ATTENTION, label: 'Needs attention', icon: AlertIcon},
  {
    value: FILTER_STATUS_COMPLETED,
    label: 'Completed',
    icon: CheckCircleIcon,
    rawValues: [FILTER_STATUS_COMPLETED, FILTER_STATUS_IDLE],
  },
  {
    value: 'unsuccessful',
    label: 'Unsuccessful',
    icon: XCircleIcon,
    rawValues: [FILTER_STATUS_FAILED, FILTER_STATUS_TIMED_OUT, FILTER_STATUS_CANCELLED],
  },
  {value: FILTER_STATUS_QUEUED, label: 'Queued', icon: ClockIcon},
]

// --- Display slug helpers ---

/**
 * Converts a display name to a URL-friendly slug.
 * e.g. "Copilot Coding Agent" → "copilot-coding-agent", "VS Code" → "vs-code"
 */
export function toDisplaySlug(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

// --- Task helpers ---

export function getStatusOptionsForTab(activeTab: ActiveTab): FilterOption[] {
  if (activeTab === 'open') return OPEN_STATUS_OPTIONS
  if (activeTab === 'closed') return CLOSED_STATUS_OPTIONS
  return ALL_STATUS_OPTIONS
}

/**
 * Returns true if the selected agents allow the "Queued" status to be shown.
 * Queued is cloud-only, so it's hidden when ALL selected agents are explicitly
 * non-cloud built-in types (cli, vscode, etc.). Custom agents (no agentType)
 * are cloud-based and support queued.
 */
export function selectedAgentsAllowQueued(selectedAgents: string[], agentOptions: FilterOption[]): boolean {
  if (selectedAgents.length === 0) return true
  const allNonCloud = selectedAgents.every(agentValue => {
    const option = agentOptions.find(o => o.value === agentValue)
    return option?.agentType !== undefined && option.agentType !== 'cloud'
  })
  return !allNonCloud
}

/**
 * Returns consolidated status options for the given tab, with "Queued" shown
 * only when cloud agents could appear in the results. Queued is hidden when:
 * - The type filter excludes cloud, OR
 * - The agent filter only has non-cloud built-in agents selected (cli, vscode, etc.)
 * Custom agents (custom:*) are cloud-based and DO support queued.
 */
export function getConsolidatedStatusOptionsForTab(
  activeTab: ActiveTab,
  selectedTypes: string[],
  selectedAgents: string[] = [],
  agentOptions: FilterOption[] = [],
): FilterOption[] {
  const typeAllowsQueued = selectedTypes.length === 0 || selectedTypes.includes('cloud')
  const showQueued = typeAllowsQueued && selectedAgentsAllowQueued(selectedAgents, agentOptions)

  if (activeTab === 'closed') return CLOSED_STATUS_OPTIONS

  const openOptions = showQueued
    ? CONSOLIDATED_OPEN_STATUS_OPTIONS
    : CONSOLIDATED_OPEN_STATUS_OPTIONS.filter(opt => opt.value !== FILTER_STATUS_QUEUED)

  if (activeTab === 'open') return openOptions
  return [...openOptions, ...CLOSED_STATUS_OPTIONS]
}

// --- Filter query parsing/building ---

export function parseFilterQuery(query: string): ParsedFilters {
  const result: ParsedFilters = {
    status: 'all',
    detailedStatuses: [],
    creators: [],
    excludedCreators: [],
    agents: [],
    types: [],
    sort: 'newest',
    freeText: '',
  }
  const parts = query.split(/\s+/).filter(Boolean)
  const freeTextParts: string[] = []
  let hasStatusToken = false

  for (const part of parts) {
    if (part.startsWith(IS_QUALIFIER)) {
      hasStatusToken = true
      const value = part.slice(IS_QUALIFIER.length)
      if (value === 'closed') result.status = 'closed'
      else if (value === 'open') result.status = 'open'
    } else if (part.startsWith(STATUS_QUALIFIER)) {
      const value = normalizeDetailedStatus(part.slice(STATUS_QUALIFIER.length))
      if (ALL_STATUSES.has(value)) result.detailedStatuses.push(value)
    } else if (part.startsWith(`${EXCLUDE_PREFIX}${AUTHOR_QUALIFIER}`)) {
      const value = part.slice(EXCLUDE_PREFIX.length + AUTHOR_QUALIFIER.length)
      if (value) result.excludedCreators.push(value)
    } else if (part.startsWith(AUTHOR_QUALIFIER)) {
      const value = part.slice(AUTHOR_QUALIFIER.length)
      if (value) result.creators.push(value)
    } else if (part.startsWith(AGENT_QUALIFIER)) {
      const value = part.slice(AGENT_QUALIFIER.length)
      if (value) result.agents.push(value)
    } else if (part.startsWith(TYPE_QUALIFIER)) {
      const value = part.slice(TYPE_QUALIFIER.length)
      if (VALID_AGENT_TYPES.has(value)) result.types.push(value)
    } else if (part.startsWith(SORT_QUALIFIER)) {
      const value = part.slice(SORT_QUALIFIER.length)
      if (value === 'oldest') result.sort = 'oldest'
    } else {
      freeTextParts.push(part)
    }
  }

  if (!hasStatusToken) {
    result.status = 'all'
  }

  result.freeText = freeTextParts.join(' ')
  return result
}

export function buildFilterQuery(
  activeTab: ActiveTab,
  selectedCreators: string[],
  sortDirection: SortDirection,
  searchQuery: string,
  detailedStatuses: string[] = [],
  selectedAgents: string[] = [],
  currentUserLogin?: string,
  excludedCreators: string[] = [],
  selectedTypes: string[] = [],
): string {
  const parts: string[] = []
  if (activeTab !== 'all') parts.push(`${IS_QUALIFIER}${activeTab}`)
  for (const status of detailedStatuses) parts.push(`${STATUS_QUALIFIER}${normalizeDetailedStatus(status)}`)
  for (const creator of selectedCreators) {
    parts.push(`${AUTHOR_QUALIFIER}${currentUserLogin && creator === currentUserLogin ? ME_ALIAS : creator}`)
  }
  for (const creator of excludedCreators) {
    parts.push(
      `${EXCLUDE_PREFIX}${AUTHOR_QUALIFIER}${currentUserLogin && creator === currentUserLogin ? ME_ALIAS : creator}`,
    )
  }
  for (const agentType of selectedTypes) parts.push(`${TYPE_QUALIFIER}${agentType}`)
  for (const agent of selectedAgents) parts.push(`${AGENT_QUALIFIER}${agent}`)
  if (sortDirection === 'oldest') parts.push(`${SORT_QUALIFIER}oldest`)
  if (searchQuery) parts.push(searchQuery)
  return parts.join(' ')
}

// --- Task matching helpers ---

export function matchesSearch(task: UITask, query: string): boolean {
  const lower = query.toLowerCase()
  const title = task.pull?.title ?? ''
  if (title.toLowerCase().includes(lower)) return true
  if (task.name?.toLowerCase().includes(lower)) return true
  if (task.problemStatement?.toLowerCase().includes(lower)) return true
  if (task.creator_login?.toLowerCase().includes(lower)) return true
  return false
}

export function matchesDetailedStatus(task: UITask, status: string): boolean {
  status = normalizeDetailedStatus(status)
  if (status === FILTER_STATUS_NEEDS_ATTENTION) return task.latest_session_state === 'waiting_for_user'
  if (status === FILTER_STATUS_COMPLETED) {
    // A task is "completed" only if the session finished and the artifact isn't merged/closed.
    // Prefer artifact_state from the API; fall back to pull?.state only when artifact_state is absent.
    const effectiveState = task.artifact_state ?? task.pull?.state
    return task.latest_session_state === 'completed' && effectiveState !== 'merged' && effectiveState !== 'closed'
  }
  if (OPEN_STATUSES.has(status) && status !== FILTER_STATUS_NEEDS_ATTENTION && status !== FILTER_STATUS_COMPLETED)
    return task.latest_session_state === mapDetailedStatusToSessionState(status)
  if (status === FILTER_STATUS_MERGED) {
    const effectiveState = task.artifact_state ?? task.pull?.state
    return task.latest_session_state === 'completed' && effectiveState === 'merged'
  }
  if (status === FILTER_STATUS_CLOSED) {
    const effectiveState = task.artifact_state ?? task.pull?.state
    return task.latest_session_state === 'completed' && effectiveState === 'closed'
  }
  if (status === FILTER_STATUS_ARCHIVED) return !!task.archived_at
  return false
}
